import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

interface SurveyErrorFallbackProps {
  error?: Error;
  surveyId?: string;
  surveyName?: string;
  onReset: () => void;
}

const SurveyErrorFallback: React.FC<SurveyErrorFallbackProps> = ({ 
  error, 
  surveyId, 
  surveyName,
  onReset 
}) => {
  const { t } = useTranslation('survey');
  const navigate = useNavigate();

  const handleRetry = () => {
    onReset();
  };

  const handleGoHome = () => {
    navigate('/dashboard');
  };

  const handleGoToSurveys = () => {
    navigate('/assessment');
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-red-100">
            <AlertTriangle className="h-6 w-6 text-red-600" />
          </div>
          <CardTitle className="text-xl font-semibold text-gray-900">
            {t('error.title', 'Survey Error')}
          </CardTitle>
          <CardDescription className="text-gray-600">
            {surveyName ? 
              `Something went wrong while loading the survey "${surveyName}".` : 
              t('error.description', 'Something went wrong while loading the survey.')
            }
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {surveyId && (
            <div className="text-sm text-gray-500">
              <strong>{t('error.surveyId', 'Survey ID')}:</strong> {surveyId}
            </div>
          )}
          
          {process.env.NODE_ENV === 'development' && error && (
            <details className="text-xs text-gray-500">
              <summary className="cursor-pointer hover:text-gray-700">
                {t('error.details', 'Error Details')}
              </summary>
              <pre className="mt-2 whitespace-pre-wrap bg-gray-100 p-2 rounded">
                {error.message}
                {error.stack && `\n\n${error.stack}`}
              </pre>
            </details>
          )}

          <div className="flex flex-col space-y-2">
            <Button onClick={handleRetry} className="w-full">
              <RefreshCw className="mr-2 h-4 w-4" />
              {t('error.retry', 'Try Again')}
            </Button>
            
            <Button 
              variant="outline" 
              onClick={handleGoToSurveys}
              className="w-full"
            >
              {t('error.backToSurveys', 'Back to Surveys')}
            </Button>
            
            <Button 
              variant="ghost" 
              onClick={handleGoHome}
              className="w-full"
            >
              <Home className="mr-2 h-4 w-4" />
              {t('error.goHome', 'Go to Dashboard')}
            </Button>
          </div>

          <div className="text-xs text-gray-400 text-center">
            {t('error.reported', 'This error has been automatically reported to our team.')}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SurveyErrorFallback;